import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "../mobile.css";
import MobileTopBar from "./MobileTopBar";
import MobileTabBar from "./MobileTabBar";
import DockedSlipPill from "./DockedSlipPill";
import { useSheet } from "./SheetHost";
import MobileErrorBoundary from "./MobileErrorBoundary";
import MenuSheet from "../sheets/MenuSheet";
import CurrencySheet from "../sheets/CurrencySheet";
import SearchSheet from "../sheets/SearchSheet";
import BetSlipSheetV2 from "../sheets/BetSlipSheetV2";
import RedeemSheet from "../sheets/RedeemSheet";
import CoinStoreSheet from "../sheets/CoinStoreSheet";
import { ORIGINAL_PAGE_ROUTES } from "@/casino/nativeGames";

const ORIGINAL_PATHS = new Set(Object.values(ORIGINAL_PAGE_ROUTES));

/* Per-screen chrome control. A screen calls useChrome({mode,title}) to switch the
   top bar to back-mode with a title, or {bare:true} to hide all chrome (full-bleed
   game view). Default is the app top bar + tab bar. */
const ChromeCtx = createContext({ setChrome: () => {} });
export const useChrome = (opts) => {
  const { setChrome } = useContext(ChromeCtx);
  useEffect(() => {
    setChrome(opts || {});
    return () => setChrome({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(opts)]);
};

/* Sheet openers exposed to any mobile screen (works for both children and Outlet
   mounting — screens call useOpeners() instead of useOutletContext()). */
const OpenersCtx = createContext({});
export const useOpeners = () => useContext(OpenersCtx);

const TAB_FOR_PATH = (p) => {
  if (p === "/") return "home";
  if (p.startsWith("/casino")) return "casino";
  if (p.startsWith("/sportsbook") || p.startsWith("/event")) return "sports";
  if (p.startsWith("/my-bets")) return "bets";
  return "home";
};

export default function MobileShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { openSheet } = useSheet();
  const [chrome, setChrome] = useState({});
  const ctx = useMemo(() => ({ setChrome }), []);

  const activeTab = TAB_FOR_PATH(location.pathname);
  const originalPage = ORIGINAL_PATHS.has(location.pathname.replace(/\/+$/, ""));

  // Sheet openers shared by the top bar + tab bar.
  const openCurrency = () => openSheet(({ close }) => <CurrencySheet close={close} openCashier={openCashier} />, { detent: "medium", label: "Switch currency" });
  const openSearch = () => openSheet(({ close }) => <SearchSheet close={close} />, { detent: "full", label: "Search" });
  const openCashier = (tab) => (tab === "redeem"
    ? openSheet(({ close }) => <RedeemSheet close={close} openCashier={openCashier} />, { detent: "large", label: "Redeem" })
    : openSheet(({ close }) => <CoinStoreSheet close={close} openCashier={openCashier} />, { detent: "large", label: "Get Coins" }));
  const openMenu = () => openSheet(({ close }) => <MenuSheet close={close} openCashier={openCashier} />, { detent: "large", label: "Menu" });
  const openSlip = () => openSheet(({ close }) => <BetSlipSheetV2 close={close} />, { detent: "large", label: "Bet slip" });

  const bare = chrome.bare;
  // openSheet (useCallback in SheetHost) + navigate are stable, so this object is
  // stable too — but we depend on them explicitly rather than silencing the lint.
  const openers = useMemo(() => ({ openCashier, openSearch, openCurrency, openSlip, openMenu }), [openSheet, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ChromeCtx.Provider value={ctx}>
    <OpenersCtx.Provider value={openers}>
      <div className="m-app" style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", position: "relative" }}>
        {!bare && (
          <MobileTopBar
            mode={chrome.mode || "app"}
            title={chrome.title}
            onOpenCashier={() => openCashier()}
            onOpenCurrency={openCurrency}
            onOpenMenu={openMenu}
          />
        )}
        <main
          style={{
            flex: 1, minHeight: 0,
            paddingBottom: bare ? 0 : "calc(64px + var(--m-safe-bot) + 12px)",
          }}
        >
          {/* Screens read useChrome()/useOpeners() to control bars + open sheets.
              Children form is used for per-route mounting; Outlet for a layout route.
              Wrapped in an error boundary so one screen's crash can't blank the app. */}
          <MobileErrorBoundary routeKey={location.pathname}>
            {children || <Outlet context={openers} />}
          </MobileErrorBoundary>
          {!bare && originalPage && <DockedSlipPill inFlow onOpen={openSlip} />}
        </main>
        {!bare && !originalPage && <DockedSlipPill onOpen={openSlip} />}
        {!bare && (
          <MobileTabBar
            active={activeTab}
            onNavigate={(to) => navigate(to)}
            onMenu={openMenu}
          />
        )}
      </div>
    </OpenersCtx.Provider>
    </ChromeCtx.Provider>
  );
}
